import { emoteAction, emoteEmoji, iconCell, type EmoteKind, type IconPlace } from "odyssey-shared";
import type { ShownEmoji } from "../../emotes/wire.ts";
import type { FallbackLog, IconAtlasState } from "../../icons/art.ts";
import type { EmojiReport } from "./report.ts";

/**
 * What an emoji over a character draws (ADR-114 decisions 5.3 and 6.6, and its amendment of 2026-10-09): the art of an
 * icon key in the page's icon atlas. An `emojiShown` emoji's id and an `actionShown` action's id are each turned into
 * the key their row names here, at the page's edge, so the surface draws from a key and an emoji and an action's
 * picture are drawn alike. There is no glyph to fall back to: an id the page's emote table lacks and a key the pointer
 * lacks each draw nothing and are said once, and until the atlas has decoded nothing is drawn and nothing is said, the
 * loader having said any failure itself (`page-icons.ts`).
 */

/**
 * The icon key of a picture the server showed: an emoji's own key, or the emoji an action names.
 *
 * @param kind The emote's kind: `emoji` from `emojiShown`, `action` from `actionShown`.
 * @param id The emote's id within its kind.
 * @param log Where an id the table lacks is said, once per kind and id.
 * @returns The key, or null for an id this page's emote table lacks.
 * @example
 * emojiKeyOf("emoji", 1, log);
 * // "emoji-happy"
 * emojiKeyOf("action", 5, log);
 * // "emoji-laughing", Laugh's picture
 */
export function emojiKeyOf(kind: EmoteKind, id: number, log: FallbackLog): string | null {
  const key = kind === "emoji" ? emoteEmoji(id)?.key : emoteAction(id)?.emoji;
  if (key === undefined) {
    log.once(`${kind} ${id}`, `${kind} ${id} is not in this page's emote table; nothing is drawn for it`);
    return null;
  }
  return key;
}

/**
 * Where an emoji's art stands in the page's icon atlas.
 *
 * @param atlas The page's icon atlas as it stands.
 * @param key The emoji's icon key.
 * @param log Where a key the pointer lacks is said, once per key.
 * @returns The art's place, or null while the atlas loads, after it failed, or for a key its pointer lacks.
 */
export function emojiCell(atlas: IconAtlasState, key: string, log: FallbackLog): IconPlace | null {
  if (atlas.status !== "ready") {
    return null;
  }
  const place = iconCell(atlas.manifest, key);
  if (place === null) {
    log.once(`emoji key ${key}`, `the icon pointer has no ${key} (an emoji); nothing is drawn for it`);
  }
  return place;
}

/**
 * The page's way in for the server's pictures: each `emojiShown` and `actionShown` turned into its icon key and queued
 * for the canvas, one whose id this page's table lacks dropped and said once.
 *
 * @param report The emoji's report, whose queue the surface takes from.
 * @param log Where an unknown id is said.
 * @returns What the session hands each emoji to.
 * @example
 * const feed = emojiFeed(gameReport.emoji, PAGE_ICONS.log);
 * feed({ handle: 7, kind: "emoji", id: 1 });
 * // queues "emoji-happy" over handle 7
 * feed({ handle: 7, kind: "action", id: 5 });
 * // queues "emoji-laughing" over handle 7, replacing the first
 */
export function emojiFeed(report: Pick<EmojiReport, "show">, log: FallbackLog): (emoji: ShownEmoji) => void {
  return (emoji) => {
    const key = emojiKeyOf(emoji.kind, emoji.id, log);
    if (key !== null) {
      report.show(emoji.handle, key);
    }
  };
}
